import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "../data/catalog";
import { BoltIcon, BoltMark } from "../components/Bolts";
import { ArrowRight, Hammer, Shirt, Sticker, Flame, CheckCircle2, Package, Scissors, Sparkles } from "lucide-react";

const CAT_ICONS = {
    apparel: <Shirt className="w-5 h-5" />,
    stickers: <Sticker className="w-5 h-5" />,
    "vinyl-decals": <Scissors className="w-5 h-5" />,
    buttons: <BoltIcon className="w-5 h-5" />,
    woodworking: <Hammer className="w-5 h-5" />,
    "laser-engraving": <Flame className="w-5 h-5" />,
    collectibles: <Package className="w-5 h-5" />,
};

const stats = [
    { k: "1-of-1", v: "Built to order" },
    { k: "100%", v: "Hand-made" },
    { k: "10+", v: "Materials & substrates" },
    { k: "∞", v: "Custom requests welcome" },
];

const process = [
    { n: "01", t: "Pitch your idea", d: "Send a description, sketch, or reference. We'll figure out the rest." },
    { n: "02", t: "We design & quote", d: "Mock-ups, materials, pricing — clear and upfront." },
    { n: "03", t: "We build it", d: "Print, press, cut, engrave. Every piece checked by hand." },
    { n: "04", t: "Ships to you", d: "Packaged sharp. Tracked. Storm-tested." },
];

const Home = () => {
    return (
        <div data-testid="home-page">
            {/* ============== HERO ============== */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden">
                <div className="hero-bg absolute inset-0" style={{
                    backgroundImage: `linear-gradient(180deg, rgba(5,5,10,0.35) 0%, rgba(5,5,10,0.7) 60%, #05050A 100%), url("/images/hero-bg.jpg")`
                }} />
                <div className="absolute inset-0 bg-gradient-to-b from-storm-void/40 via-storm-void/55 to-storm-void" />
                <div className="absolute inset-0 animate-flash bg-white/0" style={{
                    background: "radial-gradient(ellipse at 60% 18%, rgba(255,255,255,0.45), transparent 55%)"
                }} />
                <div className="vignette" />
                <div className="relative z-10 container-x pb-16 md:pb-24 lg:pb-32 w-full">
                    <div className="max-w-4xl animate-fade-up">
                        <p className="eyebrow flex items-center gap-2 mb-5">
                            <BoltIcon className="w-3.5 h-3.5" />
                            DangerSky · dangersky.com
                        </p>
                        <p className="inline-flex items-center gap-2 text-storm-bolt text-xs uppercase tracking-[0.25em] mb-4">
                            <Sparkles className="w-3.5 h-3.5" /> New products added weekly
                        </p>
                        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.85] text-white">
                            Storm-born <br />
                            <span className="text-storm-bolt">craftsmanship.</span>
                        </h1>
                        <p className="text-storm-mist text-base md:text-lg lg:text-xl max-w-2xl mt-6 leading-relaxed">
                            Custom apparel, full-color stickers, vinyl decals, buttons,
                            woodworking and laser engraving — built one piece at a time, the
                            way real makers still do it.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-9">
                            <a href="#contact-us-now" className="btn-primary" data-testid="hero-contact-btn">
                                Contact Us Now <ArrowRight className="w-4 h-4" />
                            </a>
                            <a href="#categories" className="btn-secondary" data-testid="hero-explore-btn">
                                Explore Categories
                            </a>
                        </div>
                    </div>
                </div>
                {/* Decorative floating bolt */}
                <div className="absolute right-[-60px] top-[20%] hidden lg:block animate-drift opacity-50">
                    <BoltMark className="w-40 h-40 text-storm-bolt/40" />
                </div>
            </section>

            {/* ============== STATS / MARQUEE ============== */}
            <section className="border-y border-white/10 bg-storm-navy">
                <div className="container-x py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((s, i) => (
                        <div key={i} className="flex items-center gap-3" data-testid={`stat-${i}`}>
                            <BoltIcon className="w-5 h-5 text-storm-bolt" />
                            <div>
                                <div className="font-heading text-2xl text-white leading-none">{s.k}</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-storm-mist mt-1">{s.v}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ============== CATEGORIES BENTO ============== */}
            <section id="categories" className="relative py-24 md:py-32 container-x">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                    <div>
                        <p className="eyebrow mb-3">What we make</p>
                        <h2 className="text-4xl md:text-6xl lg:text-7xl text-white max-w-3xl">
                            Every category. <br />
                            <span className="text-storm-bolt">Fully customizable.</span>
                        </h2>
                    </div>
                    <p className="text-storm-mist max-w-md text-base md:text-lg">
                        Pick a category to see what we make — or skip ahead and request a
                        custom quote for anything wild you've got in mind.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {CATEGORIES.map((cat) => (
                        <CategoryCard
                            key={cat.slug}
                            cat={cat}
                            image={cat.image}
                            icon={CAT_ICONS[cat.slug] || <BoltIcon className="w-5 h-5" />}
                        />
                    ))}
                </div>
            </section>

            {/* ============== PROCESS ============== */}
            <section className="relative py-24 md:py-32 bg-storm-navy border-y border-white/10">
                <div className="container-x">
                    <div className="max-w-3xl mb-16">
                        <p className="eyebrow mb-3">How it works</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl text-white">
                            From idea to <span className="text-storm-bolt">in your hands.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
                        {process.map((p, i) => (
                            <div
                                key={p.n}
                                className="bg-storm-void p-7 md:p-8 group hover:bg-storm-navy transition-colors"
                                data-testid={`process-${i}`}
                            >
                                <div className="font-mono text-xs tracking-[0.3em] text-storm-bolt mb-6">
                                    {p.n}
                                </div>
                                <h3 className="text-2xl text-white mb-3">{p.t}</h3>
                                <p className="text-sm text-storm-mist leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12 flex flex-col items-center text-center gap-4">
                        <p className="text-storm-mist">Have a one-of-a-kind idea? Let's make it real.</p>
                        <Link to="/custom-request" data-testid="home-custom-request-cta" className="btn-primary">
                            <BoltIcon className="w-4 h-4" /> Click here for our custom request form
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============== PROMISE STRIP ============== */}
            <section className="container-x py-20 md:py-28">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <p className="eyebrow mb-3">Why us</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl text-white">
                            Real makers. <br />
                            Real materials. <br />
                            <span className="text-storm-bolt">Real attention.</span>
                        </h2>
                    </div>
                    <ul className="space-y-4">
                        {[
                            "One-on-one design conversations — not a faceless print farm.",
                            "Premium garments & substrates. Heavyweight, never see-through.",
                            "Full-color prints, woodworking, laser engraving — all under one roof.",
                            "Custom colors, custom sizes, custom anything. We say yes.",
                        ].map((t, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3 text-base md:text-lg text-white/90"
                                data-testid={`promise-${i}`}
                            >
                                <CheckCircle2 className="w-5 h-5 text-storm-bolt flex-shrink-0 mt-1" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </div>
    );
};

const CategoryCard = ({ cat, className = "", size = "md", image, icon }) => {
    const isLg = size === "lg";
    return (
        <Link
            to={`/${cat.slug}`}
            data-testid={`category-card-${cat.slug}`}
            className={`group relative overflow-hidden surface-solid block ${className}`}
        >
            <div className={`${isLg ? "aspect-[16/11]" : "aspect-[16/10]"} relative overflow-hidden`}>
                <img
                    src={image}
                    alt={cat.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-storm-void via-storm-void/50 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-storm-void/80 backdrop-blur-md border border-white/10 px-3 py-1.5">
                    <span className="text-storm-bolt">{icon}</span>
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white">
                        {cat.sub.length > 0 ? `${cat.sub.length} sub-categories` : "Made to order"}
                    </span>
                </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 z-10">
                <h3 className={`${isLg ? "text-4xl md:text-6xl" : "text-3xl md:text-4xl"} text-white leading-none mb-2`}>
                    {cat.name}
                </h3>
                <p className="text-sm text-storm-mist mb-4 line-clamp-2">{cat.tagline}</p>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt font-heading group-hover:gap-3 transition-all">
                    Explore <ArrowRight className="w-4 h-4" />
                </span>
            </div>
        </Link>
    );
};

export default Home;
